import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { settingValue } from '@/stores/settings.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { age, count, date } from '@/lib/format';
import {
  LANGUAGES,
  emptyPage,
  isWritten,
  pageState,
  readPage,
  seoChecks,
  seoScore,
  slugProblem,
  slugify,
  textStats,
  writePage,
  type Language,
  type PageDraft,
  type SeoCheck,
} from '@/lib/content-pages';
import { CONTENT_PAGES } from '@/lib/settings-schema';
import { storefrontUrl } from '@/lib/storefront-url';
import { BilingualField, ToggleField, readString, type BilingualValue } from '@/components/fields';
import { ExternalIcon, FileIcon, PlusIcon, SearchIcon, SparkleIcon, TrashIcon } from '@/components/icons';
import { Menu } from '@/components/menu';
import { Page, SaveBar, Section, Segmented, Sheet, Tabs } from '@/components/page';
import { Alert, Button, Card, ConfirmDialog, Pill, Select, Skeleton, cx } from '@/components/ui';
import { OwnerOnly, useSettings } from '../settings/schema';
import { RichTextEditor, type EditorHandle } from './editor';
import { Checklist, ScoreRing, SeoPanel } from './seo';

/**
 * The online store's content pages (ADR-015, ADR-058) — about, questions,
 * shipping, returns, contact, the legal pages, and any page a store adds.
 *
 * Three columns on a desktop: the pages, the open page, and its side panel.
 * The page is written in an editor with a toolbar and a live preview, one
 * language at a time with the other's state always in view; its search and
 * sharing have a tab of their own with previews of each; and the side panel
 * says whether it is live, whether the footer links it, and how it will do in
 * search, with the outline to jump around a long policy. Every page saves from
 * one bar — or ⌘S — as the one `pages.content` setting underneath.
 */

export function ContentPagesPage() {
  const t = useT();
  const [creating, setCreating] = useState(false);
  return (
    <Page
      title={t('contentPages')}
      subtitle={t('contentPagesHint')}
      width="wide"
      actions={
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="size-4" />
          {t('pageNew')}
        </Button>
      }
    >
      <OwnerOnly>
        <ContentPages creating={creating} onCreating={setCreating} />
      </OwnerOnly>
    </Page>
  );
}

type Tab = 'content' | 'seo';

const LEGAL = ['privacy', 'terms'];

/** A page compared as the editor would write it — so a page nobody touched never reads as changed. */
const normal = (value: unknown) => JSON.stringify(writePage(readPage(value)));

function ContentPages({ creating, onCreating }: { creating: boolean; onCreating: (open: boolean) => void }) {
  const { entries, loading, saving, error, save } = useSettings();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [params, setParams] = useSearchParams();
  const editor = useRef<EditorHandle>(null);

  const stored = (settingValue(entries, 'pages.content') ?? {}) as Record<string, unknown>;
  const storeName = readString((settingValue(entries, 'store.info') as { name?: unknown } | undefined)?.name);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [language, setLanguage] = useState<Language>(locale);
  const [tab, setTab] = useState<Tab>('content');
  const [justSaved, setJustSaved] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [query, setQuery] = useState('');

  const value = draft ?? stored;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(stored);

  // The usual pages first, always listed — an unwritten one is a gap to fill —
  // then every page the store added, by its address.
  const slugs = useMemo(
    () => [...CONTENT_PAGES.map((entry) => entry.slug), ...Object.keys(value).filter((slug) => !CONTENT_PAGES.some((entry) => entry.slug === slug)).sort()],
    [value],
  );
  const requested = params.get('page');
  const selected = requested && slugs.includes(requested) ? requested : slugs[0];
  const select = (slug: string) => setParams((current) => {
    const next = new URLSearchParams(current);
    next.set('page', slug);
    return next;
  }, { replace: true });

  const pages = useMemo(() => Object.fromEntries(slugs.map((slug) => [slug, readPage(value[slug])])), [slugs, value]);
  const current = pages[selected] ?? emptyPage();
  const builtIn = CONTENT_PAGES.find((entry) => entry.slug === selected);
  const nameOf = (slug: string) => {
    const page = pages[slug];
    return page?.[locale].title.trim() || page?.[locale === 'ar' ? 'en' : 'ar'].title.trim() || CONTENT_PAGES.find((entry) => entry.slug === slug)?.label[locale] || slug;
  };
  const checks = seoChecks(current, language, storeName);
  const links = slugs
    .filter((slug) => slug !== selected && pageState(pages[slug]) !== 'empty')
    .map((slug) => ({ label: pages[slug][language].title.trim() || nameOf(slug), href: `/pages/${slug}` }));

  function update(slug: string, next: PageDraft) {
    setJustSaved(false);
    setDraft({ ...value, [slug]: writePage(next) });
  }

  async function commit() {
    if (!draft || !dirty) return;
    if (await save('pages.content', draft)) {
      setDraft(null);
      setJustSaved(true);
    }
  }

  // ⌘S saves, as in every editor an operator has used.
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void commitRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading && entries.length === 0) return <Skeleton className="h-[36rem]" />;

  const state = pageState(current);
  const live = state === 'published' ? storefrontUrl(language, `/pages/${selected}`) : null;
  const edited = (slug: string) => draft !== null && normal(draft[slug]) !== normal(stored[slug]);

  return (
    <>
      <div className="grid items-start gap-5 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <PageList
          slugs={slugs}
          pages={pages}
          selected={selected}
          query={query}
          onQuery={setQuery}
          onSelect={select}
          nameOf={nameOf}
          edited={edited}
          storeName={storeName}
        />

        <div className="flex min-w-0 flex-col gap-4">
          {/* On a phone the list would push the page a screen down: a picker stands in for it. */}
          <label className="flex flex-col gap-1.5 lg:hidden">
            <span className="text-xs font-medium text-ink-muted">{t('pageChoose')}</span>
            <Select value={selected} onChange={(event) => select(event.target.value)} className="w-full">
              {slugs.map((slug) => (
                <option key={slug} value={slug}>
                  {`${nameOf(slug)} — ${pageState(pages[slug]) === 'published' ? t('pageStatusPublished') : pageState(pages[slug]) === 'draft' ? t('pageStatusDraft') : t('pageEmpty')}${edited(slug) ? ' •' : ''}`}
                </option>
              ))}
            </Select>
          </label>

          {/* The open page: what it is, where it lives, and its rarer actions. */}
          <Card className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4">
            <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-700 max-sm:hidden">
              <FileIcon className="size-5" />
            </span>
            <div className="min-w-[12rem] flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-base font-semibold text-ink">{nameOf(selected)}</h2>
                <StatusPill page={current} />
                {edited(selected) ? <Pill tone="info">{t('pageEdited')}</Pill> : null}
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-faint">
                <span dir="ltr">/pages/{selected}</span>
                {current.updatedAt ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{t('pageUpdatedAgo', { when: age(current.updatedAt, locale) })}</span>
                  </>
                ) : !(selected in stored) ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{t('pageNeverSaved')}</span>
                  </>
                ) : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {live ? (
                <a
                  href={live}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-10 items-center gap-2 rounded-control border border-line bg-surface px-3 text-sm font-medium text-ink transition-colors hover:bg-surface-sunken"
                >
                  <ExternalIcon className="size-4" />
                  {t('pageViewOnStore')}
                </a>
              ) : null}
              <Menu
                bordered
                label={t('pageActions')}
                items={[
                  {
                    label: builtIn ? t('pageClear') : t('pageDelete'),
                    icon: <TrashIcon className="size-4" />,
                    danger: true,
                    disabled: !builtIn && !(selected in value),
                    onSelect: () => setRemoving(true),
                  },
                ]}
              />
            </div>
          </Card>

          <div className="flex flex-wrap items-end justify-between gap-3">
            <Tabs<Tab>
              className="min-w-0 flex-1 basis-60"
              label={t('contentPages')}
              value={tab}
              onChange={setTab}
              items={[
                { id: 'content', label: t('pageTabContent') },
                { id: 'seo', label: t('pageTabSeo'), count: `${count(seoScore(checks), locale)}` },
              ]}
            />
            <LanguageSwitch page={current} value={language} onChange={setLanguage} />
          </div>

          <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_16rem] 2xl:grid-cols-[minmax(0,1fr)_18rem]">
            <div className="min-w-0">
              {tab === 'content' ? (
                <ContentTab
                  key={`${selected}-${language}`}
                  page={current}
                  language={language}
                  links={links}
                  editorRef={editor}
                  onChange={(next) => update(selected, next)}
                />
              ) : (
                <SeoPanel
                  page={current}
                  language={language}
                  slug={selected}
                  storeName={storeName}
                  onChange={(next) => update(selected, next)}
                />
              )}
            </div>

            <aside className="flex flex-col gap-4 xl:sticky xl:top-5">
              <PublishCard page={current} onChange={(next) => update(selected, next)} />
              <HealthCard checks={checks} language={language} onOpen={tab === 'seo' ? undefined : () => setTab('seo')} />
              {tab === 'content' ? <OutlineCard body={current[language].body} onJump={(line) => editor.current?.focusLine(line)} /> : null}
            </aside>
          </div>
        </div>
      </div>

      <SaveBar
        saving={saving === 'pages.content'}
        dirty={dirty}
        message={error ? <Alert>{error}</Alert> : justSaved ? <span className="text-success">✓ {t('saved')}</span> : undefined}
        onSave={() => void commit()}
        onDiscard={dirty ? () => setDraft(null) : undefined}
      />

      {/* Mounted only while open, so each new page starts from a clean form. */}
      {creating ? (
      <NewPageSheet
        taken={slugs}
        onClose={() => onCreating(false)}
        onCreate={(slug, title) => {
          const page = emptyPage();
          update(slug, { ...page, ar: { ...page.ar, title: title.ar }, en: { ...page.en, title: title.en } });
          select(slug);
          setTab('content');
          onCreating(false);
        }}
      />
      ) : null}

      <ConfirmDialog
        open={removing}
        title={builtIn ? t('pageClearTitle') : t('pageDeleteTitle')}
        body={builtIn ? t('pageClearBody') : t('pageDeleteBody', { page: nameOf(selected) })}
        confirmLabel={builtIn ? t('pageClear') : t('pageDelete')}
        cancelLabel={t('cancel')}
        onCancel={() => setRemoving(false)}
        onConfirm={() => {
          const next = { ...value };
          delete next[selected];
          setJustSaved(false);
          setDraft(next);
          setRemoving(false);
          if (!builtIn) select(slugs[0]);
        }}
      />
    </>
  );
}

// ─── The list ────────────────────────────────────────────────────────────────

function PageList({
  slugs,
  pages,
  selected,
  query,
  onQuery,
  onSelect,
  nameOf,
  edited,
  storeName,
}: {
  slugs: string[];
  pages: Record<string, PageDraft>;
  selected: string;
  query: string;
  onQuery: (next: string) => void;
  onSelect: (slug: string) => void;
  nameOf: (slug: string) => string;
  edited: (slug: string) => boolean;
  storeName: string;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const needle = query.trim().toLowerCase();
  const matches = (slug: string) =>
    !needle || slug.includes(needle) || LANGUAGES.some((language) => pages[slug][language].title.toLowerCase().includes(needle)) || nameOf(slug).toLowerCase().includes(needle);
  const builtIn = new Set(CONTENT_PAGES.map((entry) => entry.slug));
  const groups: { label: string; slugs: string[] }[] = [
    { label: t('pageGroupHelp'), slugs: slugs.filter((slug) => builtIn.has(slug) && !LEGAL.includes(slug)) },
    { label: t('pageGroupLegal'), slugs: slugs.filter((slug) => LEGAL.includes(slug)) },
    { label: t('pageGroupOwn'), slugs: slugs.filter((slug) => !builtIn.has(slug)) },
  ].map((group) => ({ ...group, slugs: group.slugs.filter(matches) }));
  const states = slugs.map((slug) => pageState(pages[slug]));

  return (
    <Card className="hidden overflow-hidden lg:sticky lg:top-5 lg:block">
      <div className="border-b border-line p-3">
        <label className="flex min-h-9 items-center gap-2 rounded-control border border-line bg-surface-sunken/60 px-2.5 focus-within:border-brand-500">
          <SearchIcon className="size-4 shrink-0 text-ink-faint" />
          <input
            type="search"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder={t('pageSearch')}
            aria-label={t('pageSearch')}
            className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
          />
        </label>
      </div>

      <nav aria-label={t('contentPages')} className="max-h-[calc(100vh-16rem)] overflow-y-auto py-1">
        {groups.every((group) => group.slugs.length === 0) ? (
          <p className="px-4 py-6 text-center text-sm text-ink-faint">{t('pageSearchEmpty')}</p>
        ) : null}
        {groups.map((group) =>
          group.slugs.length === 0 ? null : (
            <div key={group.label} className="py-1">
              <p className="px-4 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-ink-faint uppercase">{group.label}</p>
              <ul>
                {group.slugs.map((slug) => {
                  const page = pages[slug];
                  const active = slug === selected;
                  const score = seoScore(seoChecks(page, locale, storeName));
                  return (
                    <li key={slug}>
                      <button
                        type="button"
                        aria-current={active ? 'page' : undefined}
                        onClick={() => onSelect(slug)}
                        className={cx(
                          'group flex w-full items-center gap-2.5 border-s-2 px-4 py-2.5 text-start transition-colors',
                          active ? 'border-brand-600 bg-brand-50' : 'border-transparent hover:bg-surface-sunken',
                        )}
                      >
                        <StateDot page={page} />
                        <span className="min-w-0 flex-1">
                          <span className={cx('flex items-center gap-1.5 truncate text-sm', active ? 'font-medium text-brand-700' : 'text-ink')}>
                            <span className="truncate">{nameOf(slug)}</span>
                            {edited(slug) ? <span className="size-1.5 shrink-0 rounded-full bg-brand-500" title={t('pageEdited')} /> : null}
                          </span>
                          <span dir="ltr" className="block truncate text-start text-[11px] text-ink-faint">
                            /pages/{slug}
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-col items-end gap-1">
                          <LanguageMarks page={page} />
                          {pageState(page) !== 'empty' ? (
                            <span
                              data-numeric
                              title={t('seoScoreLabel', { score })}
                              className={cx('text-[10px] font-semibold', score >= 80 ? 'text-success' : score >= 50 ? 'text-warning' : 'text-danger')}
                            >
                              {t('seoShort', { score: count(score, locale) })}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ),
        )}
      </nav>

      <div className="flex flex-wrap gap-x-3 gap-y-1 border-t border-line px-4 py-2.5 text-[11px] text-ink-muted">
        <span data-numeric>{t('pageCountPublished', { count: count(states.filter((s) => s === 'published').length, locale) })}</span>
        <span data-numeric>{t('pageCountDraft', { count: count(states.filter((s) => s === 'draft').length, locale) })}</span>
        <span data-numeric>{t('pageCountEmpty', { count: count(states.filter((s) => s === 'empty').length, locale) })}</span>
      </div>
    </Card>
  );
}

function StateDot({ page }: { page: PageDraft }) {
  const t = useT();
  const state = pageState(page);
  const label = state === 'published' ? t('pageStatusPublished') : state === 'draft' ? t('pageStatusDraft') : t('pageEmpty');
  return (
    <span
      title={label}
      className={cx(
        'size-2 shrink-0 rounded-full',
        state === 'published' ? 'bg-success' : state === 'draft' ? 'bg-warning' : 'border border-ink-faint',
      )}
    >
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** "ع EN": which languages are written, at a glance. */
function LanguageMarks({ page }: { page: PageDraft }) {
  const t = useT();
  return (
    <span className="flex gap-0.5">
      {LANGUAGES.map((language) => (
        <span
          key={language}
          title={`${language === 'ar' ? 'العربية' : 'English'} — ${isWritten(page[language]) ? t('pageWritten') : t('pageEmpty')}`}
          className={cx(
            'inline-flex h-4 min-w-5 items-center justify-center rounded px-1 text-[9px] font-semibold',
            isWritten(page[language]) ? 'bg-success/12 text-success' : 'bg-surface-sunken text-ink-faint line-through',
          )}
        >
          {language === 'ar' ? 'ع' : 'EN'}
        </span>
      ))}
    </span>
  );
}

function StatusPill({ page }: { page: PageDraft }) {
  const t = useT();
  const state = pageState(page);
  if (state === 'empty') return <Pill tone="neutral">{t('pageEmpty')}</Pill>;
  if (state === 'draft') return <Pill tone="warning">{t('pageStatusDraft')}</Pill>;
  if (!isWritten(page.ar) || !isWritten(page.en)) return <Pill tone="warning">{t('pagePartlyWritten')}</Pill>;
  return <Pill tone="success">{t('pageStatusPublished')}</Pill>;
}

/** Arabic | English, each with whether it is written — the gap a shopper would see is never out of view. */
function LanguageSwitch({ page, value, onChange }: { page: PageDraft; value: Language; onChange: (next: Language) => void }) {
  const t = useT();
  return (
    <div role="radiogroup" aria-label={t('pageLanguage')} className="inline-flex rounded-control border border-line bg-surface-sunken p-0.5">
      {LANGUAGES.map((language) => (
        <button
          key={language}
          type="button"
          role="radio"
          aria-checked={value === language}
          onClick={() => onChange(language)}
          className={cx(
            'inline-flex items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-3 py-1.5 text-xs font-medium transition-colors',
            value === language ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
          )}
        >
          <span className={cx('size-1.5 rounded-full', isWritten(page[language]) ? 'bg-success' : 'bg-warning')} />
          {language === 'ar' ? 'العربية' : 'English'}
        </button>
      ))}
    </div>
  );
}

// ─── The content tab ─────────────────────────────────────────────────────────

function ContentTab({
  page,
  language,
  links,
  editorRef,
  onChange,
}: {
  page: PageDraft;
  language: Language;
  links: { label: string; href: string }[];
  editorRef: React.Ref<EditorHandle>;
  onChange: (next: PageDraft) => void;
}) {
  const t = useT();
  const text = page[language];
  const other: Language = language === 'ar' ? 'en' : 'ar';
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const [translating, setTranslating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const translate = async () => {
    setTranslating(true);
    setProblem(null);
    try {
      const source = page[other];
      const [title, body] = await Promise.all([
        source.title.trim() && !text.title.trim() ? translateText(source.title, other, language) : Promise.resolve(text.title),
        translateText(source.body, other, language),
      ]);
      onChange({ ...page, [language]: { ...text, title, body } });
    } catch (cause) {
      setProblem(errorMessage(cause, t('pageTranslateFailed')));
    } finally {
      setTranslating(false);
    }
  };

  // Offered only into an empty language: a translation never writes over words.
  const canTranslate = !text.body.trim() && Boolean(page[other].body.trim());

  return (
    <Card className="flex flex-col gap-4 p-5">
      <label className="flex flex-col gap-1.5">
        <span className="flex items-baseline justify-between text-sm font-medium text-ink">
          {t('pageTitle')}
          <span className="text-[11px] font-normal text-ink-faint">{language === 'ar' ? 'العربية' : 'English'}</span>
        </span>
        <input
          dir={dir}
          lang={language}
          value={text.title}
          placeholder={page[other].title || t('pageTitlePlaceholder')}
          onChange={(event) => onChange({ ...page, [language]: { ...text, title: event.target.value } })}
          className="min-h-12 w-full rounded-control border border-line bg-surface px-3 text-lg font-semibold text-ink placeholder:font-normal placeholder:text-ink-faint focus:border-brand-500"
        />
      </label>

      {problem ? <Alert>{problem}</Alert> : null}

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">{t('pageBody')}</span>
        <RichTextEditor
          editorRef={editorRef}
          value={text.body}
          language={language}
          title={text.title}
          label={`${t('pageBody')} — ${language === 'ar' ? 'العربية' : 'English'}`}
          links={links}
          onChange={(body) => onChange({ ...page, [language]: { ...text, body } })}
          aside={
            canTranslate ? (
              <Button variant="ghost" size="sm" disabled={translating} onClick={() => void translate()}>
                <SparkleIcon className="size-4" />
                {translating ? t('pageTranslating') : t(other === 'ar' ? 'pageTranslateFromArabic' : 'pageTranslateFromEnglish')}
              </Button>
            ) : null
          }
        />
      </div>
    </Card>
  );
}

/** Long bodies go a few paragraphs at a time — the assistant takes 5,000 characters a request. */
async function translateText(text: string, from: Language, to: Language): Promise<string> {
  const chunks: string[] = [];
  let current = '';
  for (const paragraph of text.split(/\n{2,}/)) {
    if (current && current.length + paragraph.length + 2 > 4500) {
      chunks.push(current);
      current = '';
    }
    current = current ? `${current}\n\n${paragraph}` : paragraph;
  }
  if (current) chunks.push(current);
  const out: string[] = [];
  for (const chunk of chunks) {
    const draft = await withSession(() => adminApi.ai.translate({ text: chunk.slice(0, 5000), from, to }));
    out.push(draft.fields.text?.[to] ?? '');
  }
  return out.join('\n\n');
}

// ─── The side panel ──────────────────────────────────────────────────────────

function PublishCard({ page, onChange }: { page: PageDraft; onChange: (next: PageDraft) => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  return (
    <Section title={t('pagePublishTitle')}>
      <div className="flex flex-col gap-3">
        <Segmented
          label={t('pageStatus')}
          value={page.status}
          onChange={(status) => onChange({ ...page, status })}
          items={[
            { id: 'published', label: t('pageStatusPublished') },
            { id: 'draft', label: t('pageStatusDraft') },
          ]}
        />
        <p className="text-xs text-ink-muted">{page.status === 'draft' ? t('pageDraftHint') : t('pagePublishedHint')}</p>
        <ToggleField
          label={t('pageInFooter')}
          hint={t('pageInFooterHint')}
          checked={page.footer}
          onChange={(footer) => onChange({ ...page, footer })}
        />
        <dl className="flex items-center justify-between text-xs">
          <dt className="text-ink-muted">{t('pageLastUpdated')}</dt>
          <dd className="text-ink">{page.updatedAt ? date(page.updatedAt, locale) : '—'}</dd>
        </dl>
      </div>
    </Section>
  );
}

/**
 * How the page will do in search, in the language being edited. Beside the
 * writing it is the score and the three things most worth doing, a click
 * from the fields that fix them; beside those fields it is the whole list,
 * worst first, answering as they are typed.
 */
function HealthCard({ checks, language, onOpen }: { checks: SeoCheck[]; language: Language; onOpen?: () => void }) {
  const t = useT();
  const open = checks.filter((check) => check.level !== 'good');
  return (
    <Section title={t('seoHealthTitle')} subtitle={language === 'ar' ? 'العربية' : 'English'}>
      <div className="flex items-center gap-3">
        <ScoreRing checks={checks} />
        <p className="text-xs text-ink-muted">{open.length === 0 ? t('seoAllGood') : t('seoToImprove', { count: open.length })}</p>
      </div>
      {onOpen ? (
        open.length > 0 ? (
          <>
            <div className="mt-4">
              <Checklist checks={open} limit={3} />
            </div>
            <Button variant="secondary" size="sm" className="mt-4 w-full" onClick={onOpen}>
              {t('seoImprove')}
            </Button>
          </>
        ) : null
      ) : (
        <div className="mt-4 border-t border-line pt-4">
          <Checklist checks={checks} />
        </div>
      )}
    </Section>
  );
}

/** The page's headings, each a jump to its line — how a long policy is moved around. */
function OutlineCard({ body, onJump }: { body: string; onJump: (line: number) => void }) {
  const t = useT();
  const { outline } = textStats(body);
  return (
    <Section title={t('pageOutline')}>
      {outline.length === 0 ? (
        <p className="text-xs text-ink-muted">{t('pageOutlineEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-0.5">
          {outline.map((entry) => (
            <li key={entry.line}>
              <button
                type="button"
                onClick={() => onJump(entry.line)}
                className={cx(
                  'w-full truncate rounded-[calc(var(--radius-control)-2px)] px-2 py-1 text-start text-xs transition-colors hover:bg-surface-sunken hover:text-ink',
                  entry.level === 2 ? 'font-medium text-ink' : 'ps-5 text-ink-muted',
                )}
              >
                {entry.text}
              </button>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

// ─── A new page ──────────────────────────────────────────────────────────────

function NewPageSheet({
  taken,
  onClose,
  onCreate,
}: {
  taken: string[];
  onClose: () => void;
  onCreate: (slug: string, title: BilingualValue) => void;
}) {
  const t = useT();
  const [title, setTitle] = useState<BilingualValue>({ ar: '', en: '' });
  const [slug, setSlug] = useState('');
  const [touched, setTouched] = useState(false);
  const [tried, setTried] = useState(false);

  const address = touched ? slug : slugify(title.en);
  const problem = slugProblem(address, taken);
  const titled = Boolean(title.ar.trim() || title.en.trim());

  const submit = () => {
    setTried(true);
    if (problem || !titled) return;
    onCreate(address, title);
  };

  return (
    <Sheet
      open
      title={t('pageNew')}
      subtitle={t('pageNewHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button onClick={submit}>{t('pageCreate')}</Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <BilingualField label={t('pageTitle')} value={title} onChange={setTitle} />
        {tried && !titled ? <p className="-mt-3 text-xs text-danger">{t('pageTitleRequired')}</p> : null}
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">{t('pageAddress')}</span>
          <span dir="ltr" className="flex min-h-10 items-center rounded-control border border-line bg-surface focus-within:border-brand-500">
            <span className="ps-3 text-sm text-ink-faint">/pages/</span>
            <input
              dir="ltr"
              value={address}
              placeholder="size-guide"
              onChange={(event) => {
                setTouched(true);
                setSlug(event.target.value.toLowerCase().replace(/\s+/g, '-'));
              }}
              className="min-w-0 flex-1 bg-transparent py-2 pe-3 text-sm text-ink outline-none"
            />
          </span>
          {(tried || touched) && problem ? (
            <span className="text-xs text-danger">{t(problem)}</span>
          ) : (
            <span className="text-xs text-ink-muted">{t('pageAddressNewHint')}</span>
          )}
        </label>
        <p className="rounded-control border border-dashed border-line bg-surface-sunken/60 p-3 text-xs text-ink-muted">{t('pageNewDraftNote')}</p>
      </form>
    </Sheet>
  );
}
